import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { faqSummary, faqData } from '../data/outdoorLightingFaqData';
// Styles are shared with the CHCA FAQ page so the two stay visually identical.
// OutdoorLightingFaq.css is imported second and holds only this page's additions.
import './ChcaFaq.css';
import './OutdoorLightingFaq.css';

const BASE_PATH = '/outdoor-lighting-faq';

function OutdoorLightingFaq() {
  const location = useLocation();
  const navigate = useNavigate();

  // Parse the active tab from the URL path.
  const getTabFromPath = useCallback(() => {
    const path = location.pathname.replace(new RegExp(`^${BASE_PATH}/?`), '');
    const parts = path.split('/').filter(Boolean);
    if (parts[0] === 'faq') return 'faq';
    return 'info';
  }, [location.pathname]);

  const [activeTab, setActiveTabState] = useState(getTabFromPath());
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [expandAll, setExpandAll] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const invalidLinkRef = useRef(null);

  // Keep state in step with back/forward navigation.
  useEffect(() => {
    setActiveTabState(getTabFromPath());
  }, [location.pathname, getTabFromPath]);

  const setActiveTab = useCallback((tab) => {
    setActiveTabState(tab);
    if (tab === 'info') navigate(`${BASE_PATH}/overview`, { replace: false });
    else if (tab === 'faq') navigate(`${BASE_PATH}/faq`, { replace: false });
  }, [navigate]);

  useEffect(() => {
    document.title = 'Outdoor Lighting FAQ';
  }, []);

  // Hash-based deep linking to a specific question.
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) return;

    setActiveTabState('faq');
    navigate(`${BASE_PATH}/faq` + location.hash, { replace: true });

    const validIds = faqData.map(item => item.id);
    if (validIds.includes(hash)) {
      setExpandedItems(prev => new Set(prev).add(hash));
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      setInvalidLink(true);
      setTimeout(() => {
        if (invalidLinkRef.current) {
          invalidLinkRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash, navigate]);

  const toggleItem = (id) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllItems = () => {
    if (expandAll) {
      setExpandedItems(new Set());
    } else {
      setExpandedItems(new Set(faqData.map(item => item.id)));
    }
    setExpandAll(!expandAll);
  };

  // Renders **bold**, bullet lists (lines starting with •) and numbered lists.
  const formatText = (text) => {
    let formatted = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    const paragraphs = formatted.split('\n\n');

    const grouped = [];
    let i = 0;
    while (i < paragraphs.length) {
      const p = paragraphs[i];
      const lines = p.split('\n');
      const isNumbered = lines.some(l => /^\d+\.\s/.test(l.trim()));
      if (isNumbered) {
        const introLines = [];
        const numberedItems = [];
        const firstNumIdx = lines.findIndex(l => /^\d+\.\s/.test(l.trim()));
        if (firstNumIdx > 0) {
          introLines.push(...lines.slice(0, firstNumIdx).filter(l => l.trim()));
        }
        const numLines = lines.slice(firstNumIdx).filter(l => l.trim());
        numberedItems.push(numLines.join(' '));
        i++;
        while (i < paragraphs.length) {
          const nextLines = paragraphs[i].split('\n').filter(l => l.trim());
          if (nextLines.length > 0 && /^\d+\.\s/.test(nextLines[0].trim())) {
            numberedItems.push(nextLines.join(' '));
            i++;
          } else {
            break;
          }
        }
        grouped.push({ type: 'ol', introLines, numberedItems });
      } else if (p.includes('•')) {
        grouped.push({ type: 'ul', paragraph: p });
        i++;
      } else {
        grouped.push({ type: 'p', paragraph: p });
        i++;
      }
    }

    return grouped.map((item, idx) => {
      if (item.type === 'ul') {
        const lines = item.paragraph.split('\n');
        const firstBulletIndex = lines.findIndex(l => l.trim().startsWith('•'));
        const introLines = lines.slice(0, firstBulletIndex).filter(l => l.trim());
        const bullets = lines.slice(firstBulletIndex).filter(l => l.trim().startsWith('•'));
        return (
          <div key={idx} className="answer-section">
            {introLines.length > 0 && (
              <p dangerouslySetInnerHTML={{ __html: introLines.join('<br/>') }} />
            )}
            <ul>
              {bullets.map((bullet, j) => (
                <li key={j} dangerouslySetInnerHTML={{ __html: bullet.replace(/^•\s*/, '').trim() }} />
              ))}
            </ul>
          </div>
        );
      }
      if (item.type === 'ol') {
        return (
          <div key={idx} className="answer-section">
            {item.introLines.length > 0 && (
              <p dangerouslySetInnerHTML={{ __html: item.introLines.join('<br/>') }} />
            )}
            <ol>
              {item.numberedItems.map((ni, j) => (
                <li key={j} dangerouslySetInnerHTML={{ __html: ni.replace(/^\d+\.\s*/, '').trim() }} />
              ))}
            </ol>
          </div>
        );
      }
      return <p key={idx} dangerouslySetInnerHTML={{ __html: item.paragraph.replace(/\n/g, '<br/>') }} />;
    });
  };

  const formatAnswer = formatText;

  const copyLink = (id) => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  return (
    <div className="chca-faq-page">
      <a href="#main-content" className="chca-skip-link">Skip to main content</a>
      <Header
        title="Outdoor Lighting FAQ"
        creditExtra="Content Provided by the Moderator’s Committee on Exterior Lighting, website design by Michael Rubenstein"
      >
        <div className="chca-header-tabs">
          <button
            className={`chca-header-tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
            aria-pressed={activeTab === 'info'}
          >
            Overview
          </button>
          <button
            className={`chca-header-tab ${activeTab === 'faq' ? 'active' : ''}`}
            onClick={() => setActiveTab('faq')}
            aria-pressed={activeTab === 'faq'}
          >
            FAQ
          </button>
        </div>
      </Header>

      <main id="main-content" tabIndex={-1} className="faq-content">

        {/* ===== OVERVIEW PANEL ===== */}
        {activeTab === 'info' && (
          <div className="faq-panel">
            <section className="summary-section">
              <div className="version-badge">{faqSummary.version} - {faqSummary.date}</div>
              <div className="summary-text">
                {formatText(faqSummary.intro)}
              </div>
            </section>
          </div>
        )}

        {/* ===== FAQ PANEL ===== */}
        {activeTab === 'faq' && (
          <div className="faq-panel">
            {invalidLink && (
              <div className="invalid-link-notice" role="alert" ref={invalidLinkRef}>
                The link you followed refers to a question that has been removed from this FAQ.
              </div>
            )}

            <section className="faq-section">
              <div className="faq-header">
                <h2>Questions &amp; Answers</h2>
                <button className="expand-toggle" onClick={toggleAllItems} aria-pressed={expandAll}>
                  {expandAll ? 'Collapse All' : 'Expand All'}
                </button>
              </div>

              <div className="faq-list">
                {faqData.map((item, idx) => {
                  // v7 groups questions into titled sections; render the section
                  // heading (h3) before the first question of each section.
                  const showSection = item.section && (idx === 0 || faqData[idx - 1].section !== item.section);
                  return (
                    <React.Fragment key={item.id}>
                      {showSection && (
                        <h3 className="faq-section-heading">{item.section}</h3>
                      )}
                      <div
                        id={item.id}
                        className={`faq-item ${expandedItems.has(item.id) ? 'expanded' : ''}`}
                      >
                        <h4 className="faq-question-heading"><button
                          className="faq-question"
                          onClick={() => toggleItem(item.id)}
                          aria-expanded={expandedItems.has(item.id)}
                        >
                          <span className="question-text">{item.question}</span>
                          <span className="expand-icon" aria-hidden="true">{expandedItems.has(item.id) ? '−' : '+'}</span>
                        </button></h4>
                        {expandedItems.has(item.id) && (
                          <div className="faq-answer">
                            {formatAnswer(item.answer)}
                            {item.image && (
                              <div className="faq-image">
                                <img src={item.image.src} alt={item.image.alt} />
                              </div>
                            )}
                            <div className="copy-link-wrapper">
                              <button
                                className="copy-link-button"
                                onClick={(e) => { e.stopPropagation(); copyLink(item.id); }}
                                aria-label={copiedId === item.id ? 'Link copied to clipboard' : 'Copy link to this question'}
                              >
                                {copiedId === item.id ? 'Copied!' : '🔗'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </section>
          </div>
        )}

        {faqSummary.disclaimer && (
          <DisclaimerFooter>
            <div dangerouslySetInnerHTML={{ __html: faqSummary.disclaimer }} />
          </DisclaimerFooter>
        )}
      </main>
    </div>
  );
}

export default OutdoorLightingFaq;
